import React, { useState, useEffect, useRef } from 'react';
import './PWAInstallPrompt.css';

const PWAInstallPrompt = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isWebAPK, setIsWebAPK] = useState(false);
  const [installError, setInstallError] = useState(null);

  // 使用 ref 存儲最新值，避免閉包陷阱（Stale Closure）
  const isIOSRef = useRef(false);
  const isStandaloneRef = useRef(false);
  // timer ref 用於清理 setTimeout，避免元件卸載後觸發 setState
  const timerRef = useRef(null);

  useEffect(() => {
    // 檢測是否在獨立模式（已安裝）— 同時檢查 standalone 與 minimal-ui
    const standaloneQuery = window.matchMedia('(display-mode: standalone)');
    const minimalUIQuery = window.matchMedia('(display-mode: minimal-ui)');
    const checkStandalone = () => {
      const matches = standaloneQuery.matches || minimalUIQuery.matches;
      isStandaloneRef.current = matches;
      setIsStandalone(matches);
    };
    checkStandalone();

    // 檢測 iOS 設備（含 iPadOS 13+，其 userAgent 顯示為 Macintosh）
    const detectIOS = () => {
      const isIOSDevice =
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      isIOSRef.current = isIOSDevice;
      setIsIOS(isIOSDevice);
      return isIOSDevice;
    };
    detectIOS();

    // 檢查 localStorage：7 天內已關閉過提示則不再顯示
    const checkDismissed = () => {
      const dismissedAt = localStorage.getItem('pwaPromptDismissed');
      if (dismissedAt) {
        const daysSinceDismiss =
          (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismiss < 7) return true;
      }
      return false;
    };

    // 偵測是否為 WebAPK 安裝（Android Chrome「安裝應用程式」方式）
    const detectWebAPK = async () => {
      try {
        if ('getInstalledRelatedApps' in navigator) {
          const relatedApps = await navigator.getInstalledRelatedApps();
          return relatedApps && relatedApps.length > 0;
        }
      } catch (err) {
        console.log('[PWA] 無法偵測已安裝的相關應用:', err);
      }
      return false;
    };

    // 監聽 beforeinstallprompt 事件
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);

      // 使用 ref 讀取最新值，避免閉包陷阱
      if (!isStandaloneRef.current && !isIOSRef.current && !checkDismissed()) {
        // 等待5秒再顯示提示，避免打擾用戶
        timerRef.current = setTimeout(() => {
          setShowPrompt(true);
        }, 5000);
      }
    };

    // 監聽應用狀態變化
    const handleAppInstalled = () => {
      console.log('PWA 已安裝到主畫面');
      setShowPrompt(false);
      setInstallPrompt(null);
      localStorage.removeItem('pwaPromptDismissed');
    };

    // 監聽獨立模式變化
    const handleDisplayModeChange = () => {
      checkStandalone();
      if (isStandaloneRef.current) {
        setShowPrompt(false);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    standaloneQuery.addEventListener('change', handleDisplayModeChange);
    minimalUIQuery.addEventListener('change', handleDisplayModeChange);

    // 初始偵測 WebAPK（僅 Android）
    if (!detectIOS()) {
      detectWebAPK().then((installed) => setIsWebAPK(installed));
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      standaloneQuery.removeEventListener('change', handleDisplayModeChange);
      minimalUIQuery.removeEventListener('change', handleDisplayModeChange);
      // 清理 setTimeout，避免元件卸載後觸發 setState
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;

    try {
      setInstallError(null);
      installPrompt.prompt();
      const choiceResult = await installPrompt.userChoice;

      if (choiceResult.outcome === 'accepted') {
        console.log('用戶接受了安裝提示');
        setShowPrompt(false);
      } else {
        console.log('用戶拒絕了安裝提示');
      }

      setInstallPrompt(null);
    } catch (error) {
      console.error('安裝失敗:', error);
      // 提供用戶可見的錯誤回饋
      setInstallError('安裝失敗，請改用瀏覽器選單中的「安裝應用程式」');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // 將提示延遲到下次（7 天內不再顯示）
    localStorage.setItem('pwaPromptDismissed', Date.now().toString());
  };

  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="pwa-install-prompt">
      <div className="pwa-install-content">
        <div className="pwa-install-header">
          <h3>📱 將打卡系統安裝到主畫面</h3>
          <button className="pwa-close-btn" onClick={handleDismiss}>&times;</button>
        </div>

        <div className="pwa-install-body">
          <p>安裝後，使用NFC打卡時將直接開啟應用，不再需要重複開啟瀏覽器！</p>

          {isIOS ? (
            <div className="ios-instructions">
              <h4>iOS 用戶安裝步驟：</h4>
              <ol>
                <li>點擊分享按鈕（📤）</li>
                <li>選擇「加入主畫面」</li>
                <li>點擊「新增」完成安裝</li>
              </ol>
              <p className="ios-note">
                ⚠️ 注意：iOS 系統限制，NFC 感應仍會先開啟 Safari，
                請安裝後從主畫面圖示開啟打卡系統以獲得完整體驗。
              </p>
            </div>
          ) : (
            <div className="android-instructions">
              <h4>安裝好處：</h4>
              <ul>
                <li>✓ NFC打卡自動開啟應用</li>
                <li>✓ 離線使用（部分功能）</li>
                <li>✓ 更快的啟動速度</li>
                <li>✓ 類似原生應用的體驗</li>
              </ul>
              {!isWebAPK && (
                <p className="android-note">
                  💡 請點擊下方「安裝到主畫面」按鈕安裝（不要使用瀏覽器選單的「加入主畫面」），
                  才能讓 NFC 感應直接喚醒應用。
                </p>
              )}
            </div>
          )}

          {installError && (
            <p className="pwa-install-error">⚠️ {installError}</p>
          )}

          <div className="pwa-install-footer">
            {!isIOS && installPrompt && (
              <button className="pwa-install-btn" onClick={handleInstallClick}>
                安裝到主畫面
              </button>
            )}
            <button className="pwa-dismiss-btn" onClick={handleDismiss}>
              稍後提醒我
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;